import { NavLink } from 'react-router-dom';
import { contracts, type MailboxReconnectReason, type Permission } from '@immo/api-contract';
import { mayCall } from '@/lib/permissions/route-gate';
import { useWorkspaceRoutes } from '@/lib/workspace-path';
import { useTranslations } from '@/i18n/use-translations';
import { useMailboxConnection } from '@/pages/settings/use-mailbox-connection';
import { MailIcon, WarningTriangleIcon } from './icons';

const VARIANT = {
  EXPIRED: {
    tone: 'bg-warning-200 text-warning-700',
    linkTone: 'hover:text-warning-700',
    titleKey: 'expired.title',
    Icon: WarningTriangleIcon,
  },
  SWITCHOVER: {
    tone: 'bg-info-50 text-info-700',
    linkTone: 'hover:text-info-700',
    titleKey: 'switchover.title',
    Icon: MailIcon,
  },
} as const satisfies Record<
  MailboxReconnectReason,
  { tone: string; linkTone: string; titleKey: string; Icon: typeof MailIcon }
>;

/**
 * App-wide reconnect CTA (IMM-86.4, AC 11). When the member's mailbox grant has
 * silently expired (e.g. a provider password change flipped it via the IMM-88.2
 * webhook), this surfaces a banner on next login pointing them at the mailbox
 * settings to reconnect. The same bar carries the switchover invitation in an
 * informational tone — that grant is healthy, so it must not read as a fault.
 * Only rendered for members who can connect a mailbox — a member without the
 * capability can't act on it, so it stays hidden.
 */
export function MailboxReconnectBanner({
  workspaceId,
  permissions,
}: {
  readonly workspaceId: string;
  readonly permissions: ReadonlyArray<Permission>;
}) {
  const t = useTranslations('pages.settings.mailbox');
  const routes = useWorkspaceRoutes();
  const canConnect = mayCall(permissions, contracts.mailbox.connect);

  // Skip the fetch entirely for members who couldn't act on it anyway.
  const { connection } = useMailboxConnection(canConnect ? workspaceId : undefined);

  if (!canConnect || connection === null || !connection.connected || connection.reconnectReason === undefined) {
    return null;
  }

  const { tone, linkTone, titleKey, Icon } = VARIANT[connection.reconnectReason];

  return (
    <div
      role="status"
      className={`flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-6 py-3 text-center text-sm font-medium ${tone}`}
    >
      <span className="inline-flex items-center gap-1.5">
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        {t(titleKey)}
      </span>
      <NavLink
        to={routes.settingsMailbox()}
        className={`underline underline-offset-2 rounded-sm focus-visible:outline-none focus-visible:shadow-focus ${linkTone}`}
      >
        {t('reconnect-cta')}
      </NavLink>
    </div>
  );
}
